// This module provides utilities for logging diagnostics related to HLS playback in the VideoPlayer component.
// To enable verbose HLS diagnostics, set VMS_HLS_DIAGNOSTICS_ENABLED to true. This will log detailed
// information about HLS events and errors to the console, which can be helpful for debugging playback issues.
// Note: Enabling verbose diagnostics may have performance implications and should generally be used only during development or troubleshooting.

// Example usage:
// import { attachHlsDiagnostics } from './videoPlayerDiagnostics';
// ...
// attachHlsDiagnostics({ hls, Hls, video, getBufferedWindow });

export const VMS_HLS_DIAGNOSTICS_ENABLED = false; // set to true to enable verbose HLS diagnostics in the console

export function logHlsDiagnostic(label, data = {}) {
  if (!VMS_HLS_DIAGNOSTICS_ENABLED) return;
  console.log(`[VMS HLS] ${label}`, data);
}

function buildHlsErrorDiagnostic(data) {
  return {
    fatal: data?.fatal,
    type: data?.type,
    details: data?.details,
    reason: data?.reason,
    error: data?.error?.message,
    frag: data?.frag
      ? {
          sn: data.frag.sn,
          url: data.frag.url,
          type: data.frag.type,
        }
      : undefined,
    response: data?.response
      ? {
          code: data.response.code,
          text: data.response.text,
        }
      : undefined,
  };
}

export function attachHlsDiagnostics({ hls, Hls, video, getBufferedWindow }) {
  if (!VMS_HLS_DIAGNOSTICS_ENABLED || !hls || !Hls || !video) return;

  hls.on(Hls.Events.MANIFEST_PARSED, () => {
    logHlsDiagnostic('manifest-parsed');
  });

  hls.on(Hls.Events.LEVEL_LOADED, (_event, data) => {
    const details = data?.details;
    const fragments = details?.fragments || [];
    const lastFragment = fragments[fragments.length - 1];
    const bufferedWindow = getBufferedWindow(video);
    const bufferedAhead =
      video && bufferedWindow && Number.isFinite(video.currentTime)
        ? bufferedWindow.end - video.currentTime
        : 0;

    logHlsDiagnostic('level-loaded', {
      live: details?.live,
      mediaSequence: details?.mediaSequence ?? details?.startSN,
      endSN: details?.endSN,
      lastFragment: lastFragment?.sn,
      fragmentCount: fragments.length,
      bufferedAhead,
      readyState: video.readyState,
      paused: video.paused,
    });
  });

  hls.on(Hls.Events.FRAG_LOADING, (_event, data) => {
    logHlsDiagnostic('frag-loading', {
      sn: data?.frag?.sn,
      url: data?.frag?.url,
      type: data?.frag?.type,
    });
  });

  hls.on(Hls.Events.FRAG_LOADED, (_event, data) => {
    logHlsDiagnostic('frag-loaded', {
      sn: data?.frag?.sn,
      stats: {
        loading: data?.stats?.loading,
        total: data?.stats?.total,
      },
    });
  });

  hls.on(Hls.Events.ERROR, (_event, data) => {
    const errorData = buildHlsErrorDiagnostic(data);

    if (data?.fatal) {
      console.error('[VMS HLS] fatal error', errorData);
      return;
    }

    console.warn('[VMS HLS] non-fatal error', errorData);
  });
}
